import { hasAdsMeasurementConsent } from '@services/consent';

export const PENDING_SIGNUP_ENHANCED_CONVERSION_KEY =
  'saf:enhanced-conversion:pending-signup-email';

const PENDING_SIGNUP_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const SHA256_HEX_PATTERN = /^[a-f0-9]{64}$/;

interface PendingSignupIdentity {
  sha256EmailAddress: string;
  expiresAt: number;
}

/**
 * Applies Google's enhanced-conversion email normalization rules without retaining the raw value.
 * Gmail and Googlemail ignore dots and plus aliases in the local part; other providers do not.
 */
export function normalizeEnhancedConversionEmail(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  if (!normalized || /\s/.test(normalized)) {
    return null;
  }

  const atIndex = normalized.indexOf('@');
  if (
    atIndex <= 0 ||
    atIndex !== normalized.lastIndexOf('@') ||
    atIndex === normalized.length - 1
  ) {
    return null;
  }

  let localPart = normalized.slice(0, atIndex);
  const domain = normalized.slice(atIndex + 1);
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    localPart = localPart.split('+', 1)[0].replaceAll('.', '');
  }

  return localPart ? `${localPart}@${domain}` : null;
}

export function isSha256EmailAddress(value: string | null | undefined): value is string {
  return typeof value === 'string' && SHA256_HEX_PATTERN.test(value);
}

async function sha256Hex(value: string): Promise<string | null> {
  if (!value || !globalThis.crypto?.subtle) {
    return null;
  }

  try {
    const bytes = new TextEncoder().encode(value);
    const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return null;
  }
}

export async function hashEnhancedConversionEmail(email: string): Promise<string | null> {
  const normalized = normalizeEnhancedConversionEmail(email);
  return normalized ? sha256Hex(normalized) : null;
}

/**
 * Produces a unique, non-identifying Google Ads transaction id for an upload. Upload ids can embed
 * an internal account id, so the raw value must never be sent to an advertising destination.
 */
export async function hashGoogleAdsTransactionId(uploadId: string): Promise<string | null> {
  const normalized = uploadId.trim();
  return normalized ? sha256Hex(normalized) : null;
}

export function clearPendingSignupEnhancedConversionIdentity(): void {
  try {
    localStorage.removeItem(PENDING_SIGNUP_ENHANCED_CONVERSION_KEY);
  } catch {
    // Enhanced identity is optional and storage can be unavailable in hardened browsers.
  }
}

/**
 * Remembers only a short-lived hash across the email-confirmation navigation. Raw signup email is
 * never persisted. A genuine ads-measurement choice is required; temporary Advanced Consent Mode
 * deliberately does not qualify.
 */
export async function rememberPendingSignupEnhancedConversionIdentity(
  email: string,
): Promise<void> {
  if (!hasAdsMeasurementConsent()) {
    clearPendingSignupEnhancedConversionIdentity();
    return;
  }

  const sha256EmailAddress = await hashEnhancedConversionEmail(email);
  // Consent may have changed while Web Crypto was running.
  if (!sha256EmailAddress || !hasAdsMeasurementConsent()) {
    clearPendingSignupEnhancedConversionIdentity();
    return;
  }

  const pending: PendingSignupIdentity = {
    sha256EmailAddress,
    expiresAt: Date.now() + PENDING_SIGNUP_TTL_MS,
  };
  try {
    localStorage.setItem(PENDING_SIGNUP_ENHANCED_CONVERSION_KEY, JSON.stringify(pending));
  } catch {
    // The later signup conversion will still be sent without enhanced identity.
  }
}

/** Consumes the pending hash exactly once and only while real ads-measurement consent is active. */
export function consumePendingSignupEnhancedConversionIdentity(): string | null {
  let serialized: string | null = null;
  try {
    serialized = localStorage.getItem(PENDING_SIGNUP_ENHANCED_CONVERSION_KEY);
    localStorage.removeItem(PENDING_SIGNUP_ENHANCED_CONVERSION_KEY);
  } catch {
    return null;
  }

  if (!serialized || !hasAdsMeasurementConsent()) {
    return null;
  }

  try {
    const pending = JSON.parse(serialized) as Partial<PendingSignupIdentity>;
    if (
      typeof pending.expiresAt !== 'number' ||
      pending.expiresAt <= Date.now() ||
      !isSha256EmailAddress(pending.sha256EmailAddress)
    ) {
      return null;
    }
    return pending.sha256EmailAddress;
  } catch {
    return null;
  }
}
